/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Col, Form, InputNumber, Modal, Row, Select, Skeleton, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
    addCustomerSuggestedPrice,
    updateCustomerSuggestedPrice,
} from '../../../../redux/actions/customers';
import { getProductDetails, getProducts } from '../../../../redux/actions/products';
import { formatCurrency, getProductImageUrl } from '../../../../utils';

const SuggestedPrice = ({ open, onClose, customer, data }) => {
    const [form] = Form.useForm();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [priceLevels, setPriceLevels] = useState(null);
    const PRODUCT_IMG = !!priceLevels?.length && getProductImageUrl(priceLevels[0]);

    const selectedProductId = Form.useWatch('product_netsuite_id', form);
    const selectedPriceLevel = Form.useWatch('price_level', form);

    const fetchProducts = async () => {
        const res = await getProducts();
        if (res?.ack) {
            setProducts(res.products);
        }
    };

    const getProductPriceLevels = useCallback(
        async (id) => {
            form.resetFields(['price_level']);
            try {
                setLoading(true);
                const res = await getProductDetails({ id, details: 1, loading: false });
                if (data) {
                    form.setFieldsValue({ price: data?.price, price_level: data?.price_level });
                }
                if (res?.ack) setPriceLevels(res?.product);
            } finally {
                setLoading(false);
            }
        },
        [form, data]
    );

    const handleTierPriceChange = (value) => {
        if (value !== 'Custom') {
            const selectedPriceLevel = priceLevels.find((price) => price.price_level === value);
            form.setFieldsValue({ price: selectedPriceLevel.unit_price });
        } else {
            form.setFieldsValue({ price: null });
        }
    };

    const handleFinish = async (values) => {
        const body = {
            ...values,
            customer_netsuite_id: customer?.customer_netsuite_id,
            customer_code: customer?.code,
            customer_name: customer?.title,
            product_code: priceLevels[0]?.code,
            product_name: priceLevels[0]?.name,
        };
        if (data) body.id = data?.id;

        const res = data
            ? await updateCustomerSuggestedPrice(body)
            : await addCustomerSuggestedPrice(body);
        if (res?.ack) onClose();
    };

    useEffect(() => {
        fetchProducts();
        if (data) {
            getProductPriceLevels(data?.netsuite_id);
            form.setFieldsValue({
                product_netsuite_id: data?.netsuite_id,
            });
        }
    }, [data]);

    return (
        <Modal
            title={customer ? `[${customer.code}] ${customer?.title ?? ''}` : 'Suggested Price'}
            open={open}
            onCancel={onClose}
            centered
            width={800}
            okText={data ? "Update" : "Save"}
            onOk={() => form.submit()}
        >
            <Form
                layout="vertical"
                form={form}
                name="suggested_price"
                size="large"
                className="tight-form"
                onFinish={handleFinish}
            >
                {selectedProductId && <Avatar shape="square" size={100} src={PRODUCT_IMG} />}
                <Form.Item name="product_netsuite_id" label="Product" rules={[{ required: true }]}>
                    <Select
                        placeholder="Search Product"
                        optionLabelProp="label"
                        showSearch
                        filterOption={(input, option) =>
                            String(option['data-title'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                            String(option['data-code'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={getProductPriceLevels}
                    >
                        {products?.map((product, idx) => (
                            <Select.Option
                                key={idx}
                                value={product?.netsuite_id}
                                label={`[${product?.code}] ${product?.name}`}
                                data-code={product?.code}
                                data-title={product?.name}
                            >
                                <Typography.Text>
                                    [{`${product?.code}`}] {product?.name}
                                </Typography.Text>
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedProductId && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="price_level" label="Price Level" rules={[{ required: true }]}>
                                {loading ? (
                                    <Skeleton.Input active className="w-full" />
                                ) : (
                                    <Select
                                        placeholder="Price Level"
                                        onChange={handleTierPriceChange}
                                        optionLabelProp="label"
                                    >
                                        {priceLevels?.map((product, idx) => (
                                            <Select.Option
                                                key={idx}
                                                value={product.price_level}
                                                label={product.price_level}
                                            >
                                                <Typography.Text>
                                                    {product.price_level} - {formatCurrency(product?.unit_price)}
                                                </Typography.Text>
                                            </Select.Option>
                                        ))}

                                        <Select.Option value="Custom" label="Custom Price">
                                            Custom Price
                                        </Select.Option>
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="price"
                                label="Price"
                                rules={[
                                    {
                                        required: selectedPriceLevel === 'Custom',
                                        message: 'Please enter price',
                                    },
                                ]}
                            >
                                {loading ? (
                                    <Skeleton.Input active className="w-full" />
                                ) : (
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        controls={false}
                                        readOnly={selectedPriceLevel !== 'Custom'}
                                        prefix="£"
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Form>
        </Modal>
    );
};

export default SuggestedPrice;
